import * as z from "zod";

import { CodeEditorLanguage } from "components/CodeEditor/helpers";

const diffEntrySchema = z.object({ before: z.json(), after: z.json() });

export type DiffEntry = z.infer<typeof diffEntrySchema>;
type Diff = Record<string, DiffEntry>;

const DEFAULT_RESULT: Diff = {};
const DEFAULT_DIFF_LABEL = "Diff";

const singleDiffSchema = diffEntrySchema.extend({
  before_header: z.string().nullish(),
  after_header: z.string().nullish(),
});

const pluralDiffSideSchema = z.record(z.string(), z.record(z.string(), z.json()));

type PluralDiffSide = z.infer<typeof pluralDiffSideSchema>;

const flattenPluralDiffSide = (side: PluralDiffSide): Record<string, DiffEntry["before"]> =>
  Object.fromEntries(
    Object.entries(side).flatMap(([file, attributes]) =>
      Object.entries(attributes).map(([attribute, value]) => [`${file} (${attribute})`, value])
    )
  );

const loopedDiffSchema = z.record(z.string(), z.unknown());

const parseJson = (value: string): unknown => {
  try {
    return JSON.parse(value);
  } catch {
    return undefined;
  }
};

const splitSingleOrPluralDiff = (diff: z.infer<typeof singleDiffSchema>): Diff => {
  const header = diff.after_header || diff.before_header;
  const entry = { before: diff.before, after: diff.after };

  if (header) {
    return { [header]: entry };
  }

  const before = pluralDiffSideSchema.safeParse(diff.before);
  const after = pluralDiffSideSchema.safeParse(diff.after);

  // shortcut: a header-less single diff holding only objects also matches; a backend diff type field would end the guess
  if (before.success && after.success) {
    const beforeSide = flattenPluralDiffSide(before.data);
    const afterSide = flattenPluralDiffSide(after.data);
    const labels = new Set([...Object.keys(beforeSide), ...Object.keys(afterSide)]);

    return Object.fromEntries(
      [...labels].map((label) => [
        label,
        {
          before: Object.hasOwn(beforeSide, label) ? beforeSide[label] : "",
          after: Object.hasOwn(afterSide, label) ? afterSide[label] : "",
        },
      ])
    );
  }

  return { [DEFAULT_DIFF_LABEL]: entry };
};

const normalizeSingleOrPluralDiff = (diff: z.infer<typeof singleDiffSchema>): Diff =>
  Object.fromEntries(
    Object.entries(splitSingleOrPluralDiff(diff)).filter(
      ([, { before, after }]) => before !== "" || after !== ""
    )
  );

export const formatDiffSide = (value: DiffEntry["before"]) =>
  typeof value === "string" ? value : JSON.stringify(value, null, 2);

const isTextSide = (value: DiffEntry["before"]) => typeof value === "string" && value !== "";

export const getDiffLanguage = ({ before, after }: DiffEntry): CodeEditorLanguage =>
  isTextSide(before) || isTextSide(after) ? "plaintext" : "json";

export const parseDiff = (diff: string | undefined): Diff => {
  if (!diff) {
    return DEFAULT_RESULT;
  }

  const parsedDiff = parseJson(diff);

  const singleOrPluralDiff = singleDiffSchema.safeParse(parsedDiff);
  if (singleOrPluralDiff.success) {
    return normalizeSingleOrPluralDiff(singleOrPluralDiff.data);
  }

  const loopedDiff = loopedDiffSchema.safeParse(parsedDiff);
  if (!loopedDiff.success) {
    return DEFAULT_RESULT;
  }

  return Object.fromEntries(
    Object.entries(loopedDiff.data).flatMap(([item, value]) => {
      const itemDiff = singleDiffSchema.safeParse(value);
      if (!itemDiff.success) {
        return [];
      }

      const entries = Object.entries(normalizeSingleOrPluralDiff(itemDiff.data));
      return entries.map(([label, entry]) => [
        entries.length === 1 ? item : `${item}: ${label}`,
        entry,
      ]);
    })
  );
};

export const prettify = (value: string | number | boolean | undefined) => {
  if (typeof value !== "string") {
    return String(value);
  }

  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};
